import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { useLabels, useRequestData } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import {
	HighlightedAttribute,
	getFuelTypeNote,
	isValidFuelType,
	isInTransit,
	getInTransitLabel,
	getVehicleStatusNote,
	getCityStateNote,
	QuickNotesButton,
	highlightedAttrList
} from 'wsm-highlighted-attribute';
import { getAttributeValue } from '../../../utilities/highlightedAttributes';
import { selectIsListLayout } from '../../../utilities/layout';
import { checkConcaternateAttr } from '../../../utilities/concatenateAttributeCheck';
import { sortQuickNotes } from '../../../utilities/sortQuickNotes';

const highlightedAttributeFactory = ({
	type = '',
	content = '',
	isListLayout,
	badgeType = 'default'
}) => {
	return (
		<HighlightedAttribute
			type={type}
			badgeClasses={[badgeType, 'mr-2', isListLayout ? '' : 'my-3']}
		>
			{content}
		</HighlightedAttribute>
	);
};

const HighlightedAttributeContainer = ({
	accountInfo,
	highlightedAttributes,
	vehicleClassification = '',
	hasInTransitBadge,
	isNewCarBoost,
	isSpotlightedVehicle,
	vehicleType
}) => {
	const isListLayout = useSelector((state) => selectIsListLayout(state));
	const { pageAlias } = useSelector((state) => state.widgetInfo);
	const labels = useLabels();
	const { deviceType, locale } = useRequestData();
	const isMobile = deviceType === 'MOBILE';
	const vehicleAccountName = accountInfo?.name || '';

	let fuelTypeAttribute;
	const quickNotesAttrs = [];
	let highlightedAttrsRenderOrder = [];

	const fuelType = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.FUEL_TYPE
	);
	if (isValidFuelType(fuelType)) {
		const fuelTypeNote = getFuelTypeNote({
			fuelType,
			labels
		});
		setNewRelicCustomAttribute('fuel-highlighted-attribute', true);
		fuelTypeAttribute = highlightedAttributeFactory({
			type: 'fuelType',
			content: fuelType,
			isListLayout
		});
		quickNotesAttrs.push({
			type: 'fuelType',
			highlightedAttr: fuelTypeAttribute,
			note: fuelTypeNote
		});
	}

	// check cityStateAttribute
	let cityStateLabelAttribute;
	let cityState = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.CITY_STATE
	);
	if (cityState) {
		cityState = cityState.replace('/', ', ').trim();
		const address = {
			...accountInfo.address,
			address1: accountInfo.address.firstLineAddress,
			address2: accountInfo.address.secondLineAddress
		};
		const cityStateNoteAttribute = getCityStateNote(cityState, labels, {
			address
		});

		cityStateLabelAttribute = highlightedAttributeFactory({
			type: highlightedAttrList.CITY_STATE,
			content: cityState,
			isListLayout
		});
		quickNotesAttrs.push({
			type: highlightedAttrList.CITY_STATE,
			highlightedAttr: cityStateLabelAttribute,
			note: cityStateNoteAttribute
		});
	}

	let locationAttribute;
	const account = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.LOCATION
	);

	if (account?.name) {
		const cleanLocationTitle = window?._?.unescape(account.name);
		let addressHTML;

		locationAttribute = highlightedAttributeFactory({
			type: 'location',
			content: cleanLocationTitle,
			isListLayout
		});

		if (account?.address && Object.keys(account?.address).length !== 0) {
			const { firstLineAddress, city, state, postalCode } =
				account.address;
			addressHTML = (
				<>
					{firstLineAddress}, {city}, {state} {postalCode}
				</>
			);
		}

		if (account?.phone) {
			const locationPhoneHTML = isMobile ? (
				<a
					href={`tel:${account.phone}`}
					data-phone-ref="SALES"
					data-account-ref={account.id}
				>
					{account.phone}
				</a>
			) : (
				account.phone
			);

			addressHTML = (
				<>
					{addressHTML}
					<span className="d-block mt-3">{locationPhoneHTML}</span>
				</>
			);
		}

		if (locationAttribute || addressHTML) {
			quickNotesAttrs.push({
				type: 'location',
				highlightedAttr: locationAttribute,
				note: addressHTML
			});
		}
	}

	let inTransitAttribute;
	const inTransitData = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.ESTIMATED_DELIVERY_DATE
	);

	if (isInTransit(inTransitData?.status)) {
		const { estimatedDeliveryDate } = inTransitData;
		const inTransitText = getInTransitLabel({
			estimatedDeliveryDate,
			locale,
			labels
		});

		inTransitAttribute = highlightedAttributeFactory({
			type: 'inTransit',
			content: inTransitText,
			isListLayout
		});
		quickNotesAttrs.push({
			type: 'inTransit',
			highlightedAttr: inTransitAttribute,
			note: getVehicleStatusNote(
				highlightedAttrList.ESTIMATED_DELIVERY_DATE,
				vehicleAccountName,
				labels
			)
		});
	}

	let odometerAttribute;
	const odometer = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.ODOMETER
	);

	/**
	 * When vehicle don't have odometer or it's value is 0, the value is still return with odometer unit 'miles', 'km', etc.
	 * Need use regex to check and only display highlighted odometer have formated odometer value and odometer unit
	 * The value is valid if contains odometer value from 1 to 3 digits or if number have more than 3 digits,
	 * we have comma for each 3 degits group.
	 */
	const isValidOdometer = checkConcaternateAttr(
		highlightedAttrList.ODOMETER,
		odometer
	);

	if (!isListLayout && isValidOdometer) {
		odometerAttribute = highlightedAttributeFactory({
			type: 'odometer',
			content: odometer,
			isListLayout
		});

		quickNotesAttrs.push({
			type: 'odometer',
			highlightedAttr: odometerAttribute,
			note: labels.get('HIGHLIGHTED_ATTR_ODOMETER_EXPLAIN')
		});
	}

	let certifiedAttribute;
	const certified = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.CERTIFIED
	);

	if (certified) {
		certifiedAttribute = highlightedAttributeFactory({
			type: 'certified',
			content: certified,
			isListLayout
		});
		quickNotesAttrs.push({
			type: 'certified',
			highlightedAttr: certifiedAttribute,
			note: labels.get('HIGHLIGHTED_ATTR_CERTIFIED_EXPLAIN')
		});
	}

	let vehicleConditionAttribute;
	if (isNewCarBoost) {
		const newLabel = labels.get('NEW');
		vehicleConditionAttribute = highlightedAttributeFactory({
			type: newLabel,
			content: newLabel,
			isListLayout
		});
		quickNotesAttrs.push({
			type: newLabel,
			highlightedAttr: vehicleConditionAttribute,
			note: labels.get('HIGHLIGHTED_ATTR_NEW_EXPLAIN')
		});
	} else if (pageAlias.includes('_AUTO_ALL')) {
		const vehicleCondition = getAttributeValue(
			highlightedAttributes,
			highlightedAttrList.CONDITION
		);
		if (vehicleCondition && vehicleType) {
			vehicleConditionAttribute = highlightedAttributeFactory({
				type: 'condition',
				content: vehicleCondition,
				isListLayout
			});

			quickNotesAttrs.push({
				type: 'condition',
				highlightedAttr: vehicleConditionAttribute,
				note: labels.get(
					// vehicleType will always be either 'new' or 'used'
					`HIGHLIGHTED_ATTR_${vehicleType.toUpperCase()}_EXPLAIN`
				)
			});
		}
	}

	let vehicleOffSiteAttribute;
	let vehicleOnSiteAttribute;
	// Onsite or Offsite highlighted attribute should only be shown if the vehicle is not in transit.
	if (!hasInTransitBadge && !isInTransit(inTransitData?.status)) {
		const vehicleOffSite = getAttributeValue(
			highlightedAttributes,
			highlightedAttrList.OFFSITE
		);
		if (vehicleOffSite) {
			vehicleOffSiteAttribute = highlightedAttributeFactory({
				type: vehicleOffSite,
				content: vehicleOffSite,
				isListLayout,
				badgeType: 'default'
			});
			quickNotesAttrs.push({
				type: vehicleOffSite,
				highlightedAttr: vehicleOffSiteAttribute,
				note: getVehicleStatusNote(
					highlightedAttrList.OFFSITE,
					vehicleAccountName,
					labels
				)
			});
		}

		const vehicleOnSite = getAttributeValue(
			highlightedAttributes,
			highlightedAttrList.ONSITE
		);
		if (vehicleOnSite) {
			vehicleOnSiteAttribute = highlightedAttributeFactory({
				type: vehicleOnSite,
				content: vehicleOnSite,
				isListLayout,
				badgeType: 'in-stock'
			});

			quickNotesAttrs.push({
				type: vehicleOnSite,
				highlightedAttr: vehicleOnSiteAttribute,
				note: getVehicleStatusNote(
					highlightedAttrList.ONSITE,
					vehicleAccountName,
					labels
				)
			});
		}
	}

	let vehicleSpotlightAttribute;
	if (isSpotlightedVehicle) {
		const featuredLabel = labels.get('FEATURED');
		vehicleSpotlightAttribute = highlightedAttributeFactory({
			type: featuredLabel,
			content: featuredLabel,
			isListLayout,
			badgeType: 'hotcars'
		});
		quickNotesAttrs.push({
			type: featuredLabel,
			highlightedAttr: vehicleSpotlightAttribute,
			note: labels.get('HIGHLIGHTED_ATTR_FEATURED_EXPLAIN')
		});
	}

	let classificationAttribute;
	const classification = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.CLASSIFICATION
	);
	if (classification && vehicleClassification !== 'primary') {
		classificationAttribute = highlightedAttributeFactory({
			type: 'classification',
			content: classification,
			isListLayout
		});
		quickNotesAttrs.push({
			type: 'classification',
			highlightedAttr: classificationAttribute,
			note: labels.get(
				`HIGHLIGHTED_ATTR_${vehicleClassification.toUpperCase()}_${vehicleType.toUpperCase()}_EXPLAIN`
			)
		});
	}

	/* The order in which the highlighted attributes will be rendered inside of the vehicle card.
	Any changes made to the order here will also be updated in the 'Quick Notes' modal. */
	highlightedAttrsRenderOrder = [
		vehicleSpotlightAttribute,
		vehicleOnSiteAttribute,
		vehicleOffSiteAttribute,
		inTransitAttribute,
		classificationAttribute,
		certifiedAttribute,
		vehicleConditionAttribute,
		odometerAttribute,
		locationAttribute,
		cityStateLabelAttribute,
		fuelTypeAttribute
	];
	const isAttrEnabled = highlightedAttrsRenderOrder.some((attr) =>
		Boolean(attr)
	);

	if (isAttrEnabled) {
		return (
			<QuickNotesButton
				quickNotesAttrs={sortQuickNotes(
					highlightedAttrsRenderOrder,
					quickNotesAttrs
				)}
			>
				<div
					className={setClassNames([
						'vehicle-card-highlight',
						isListLayout ? 'mb-3' : 'mb-0',
						'align-self-start',
						'd-flex',
						'flex-wrap'
					])}
				>
					{highlightedAttrsRenderOrder.map((attribute) =>
						attribute ? (
							<React.Fragment key={attribute.props.type}>
								{attribute}
							</React.Fragment>
						) : null
					)}
				</div>
			</QuickNotesButton>
		);
	}
	return null;
};

HighlightedAttributeContainer.propTypes = {
	accountInfo: PropTypes.shape({
		name: PropTypes.string,
		address: PropTypes.shape({
			city: PropTypes.string,
			state: PropTypes.string,
			firstLineAddress: PropTypes.string,
			secondLineAddress: PropTypes.string
		})
	}),
	hasInTransitBadge: PropTypes.bool,
	highlightedAttributes: PropTypes.arrayOf(PropTypes.shape({})),
	vehicleClassification: PropTypes.string,
	isNewCarBoost: PropTypes.bool,
	isSpotlightedVehicle: PropTypes.bool,
	vehicleType: PropTypes.string
};

export default HighlightedAttributeContainer;
